/*
 * Copyright 2018 The Kubeflow Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type * as React from 'react';
import BusyButton from '../../atoms/BusyButton';
import Viewer, { ViewerConfig } from './Viewer';
import { Apis } from '../../lib/Apis';
import { commonCss, padding, color } from '../../Css';
import { classes, stylesheet } from 'typestyle';

import {
  Button,
  InputLabel,
  Input,
  MenuItem,
  ListSubheader,
  FormControl,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

export const css = stylesheet({
  button: {
    marginBottom: 20,
    width: 150,
  },
  formControl: {
    minWidth: 120,
  },
  select: {
    minHeight: 50,
  },
  shortButton: {
    width: 50,
  },
  warningText: {
    color: color.warningText,
  },
  errorText: {
    color: color.errorText,
  },
});

export interface TensorboardViewerConfig extends ViewerConfig {
  url: string;
  namespace: string;
  podTemplateSpec?: any; // JSON object of pod template spec
  image?: string;
}

interface TensorboardViewerProps {
  configs: TensorboardViewerConfig[];
  // Interval in ms. If not specified, default to 5000.
  intervalOfCheckingTensorboardPodStatus?: number;
}

interface TensorboardViewerState {
  busy: boolean;
  deleteDialogOpen: boolean;
  proxyPath: string;
  tfImage: string;
  // When proxyPath is not null, we need to further tell whether the TensorBoard pod is accessible or not
  tensorboardReady: boolean;
  errorMessage?: string;
}

// TODO: bump default version when https://github.com/kubeflow/pipelines/issues/5521
// is resolved.
const DEFAULT_TF_IMAGE = 'tensorflow/tensorflow:2.2.2';

class TensorboardViewer extends Viewer<TensorboardViewerProps, TensorboardViewerState> {
  timerID: NodeJS.Timeout;
  private _isMounted = true;

  constructor(props: any) {
    super(props);

    this.state = {
      busy: false,
      deleteDialogOpen: false,
      proxyPath: '',
      tfImage: this._image() || DEFAULT_TF_IMAGE,
      tensorboardReady: false,
      errorMessage: undefined,
    };
  }

  public getDisplayName(): string {
    return 'Tensorboard';
  }

  public isAggregatable(): boolean {
    return true;
  }

  public componentDidMount(): void {
    this._isMounted = true;
    this._checkTensorboardApp();
    this.timerID = setInterval(
      () => this._checkTensorboardPodStatus(),
      this.props.intervalOfCheckingTensorboardPodStatus || 5000,
    );
  }

  public componentWillUnmount(): void {
    this._isMounted = false;
    clearInterval(this.timerID);
  }

  public handleImageSelect = (e: SelectChangeEvent<string>): void => {
    if (typeof e.target.value !== 'string') {
      throw new Error('Invalid event value type, expected string');
    }
    this.setStateSafe({ tfImage: e.target.value });
  };

  public render(): React.JSX.Element {
    return (
      <div>
        {this.state.errorMessage && <div className={css.errorText}>{this.state.errorMessage}</div>}
        {this.state.proxyPath && (
          <div>
            <div
              className={padding(20, 'b')}
            >{`Tensorboard ${this.state.tfImage} is running for this output.`}</div>
            <a
              href={this.state.proxyPath}
              target='_blank'
              rel='noopener noreferrer'
              className={commonCss.unstyled}
            >
              <Button
                className={classes(commonCss.buttonAction, css.button)}
                disabled={this.state.busy}
                color={'primary'}
              >
                Open Tensorboard
              </Button>
              {this.state.tensorboardReady ? (
                ``
              ) : (
                <div className={css.warningText}>
                  Tensorboard is starting, and you may need to wait for a few minutes.
                </div>
              )}
            </a>

            <div>
              <Button
                className={css.button}
                disabled={this.state.busy}
                id={'delete'}
                title={`stop tensorboard and delete its instance`}
                onClick={this._handleDeleteOpen}
              >
                Stop Tensorboard
              </Button>
              <Dialog
                open={this.state.deleteDialogOpen}
                onClose={this._handleDeleteClose}
                aria-labelledby='dialog-title'
              >
                <DialogTitle id='dialog-title'>{`Stop Tensorboard?`}</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    You can stop the current running tensorboard. The tensorboard viewer will also
                    be deleted from your workloads.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    className={css.shortButton}
                    id={'cancel'}
                    autoFocus={true}
                    onClick={this._handleDeleteClose}
                    color='primary'
                  >
                    Cancel
                  </Button>
                  <BusyButton
                    className={classes(commonCss.buttonAction, css.shortButton)}
                    onClick={this._deleteTensorboard}
                    busy={this.state.busy}
                    color='primary'
                    title={`Stop`}
                  />
                </DialogActions>
              </Dialog>
            </div>
          </div>
        )}
        {!this.state.proxyPath && (
          <div>
            <div className={padding(30, 'b')}>
              <FormControl variant='standard' className={css.formControl}>
                <InputLabel htmlFor='viewer-tb-image-select'>TF Image</InputLabel>
                <Select
                  variant='standard'
                  className={css.select}
                  value={this.state.tfImage}
                  input={<Input id='viewer-tb-image-select' />}
                  onChange={this.handleImageSelect}
                >
                  {this._image() && <MenuItem value={this._image()}>{this._image()}</MenuItem>}
                  <ListSubheader>Tensoflow 1.x</ListSubheader>
                  <MenuItem value={'tensorflow/tensorflow:1.7.1'}>TensorFlow 1.7.1</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:1.8.0'}>TensorFlow 1.8.0</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:1.9.0'}>TensorFlow 1.9.0</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:1.10.1'}>TensorFlow 1.10.1</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:1.11.0'}>TensorFlow 1.11.0</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:1.12.3'}>TensorFlow 1.12.3</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:1.13.2'}>TensorFlow 1.13.2</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:1.14.0'}>TensorFlow 1.14.0</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:1.15.5'}>TensorFlow 1.15.5</MenuItem>
                  <ListSubheader>TensorFlow 2.x</ListSubheader>
                  <MenuItem value={'tensorflow/tensorflow:2.0.4'}>TensorFlow 2.0.4</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:2.1.2'}>TensorFlow 2.1.2</MenuItem>
                  <MenuItem value={'tensorflow/tensorflow:2.2.2'}>TensorFlow 2.2.2</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div>
              <BusyButton
                className={commonCss.buttonAction}
                disabled={!this.state.tfImage}
                onClick={this._startTensorboard}
                busy={this.state.busy}
                title={`Start ${this.props.configs.length > 1 ? 'Combined ' : ''}Tensorboard`}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  private _handleDeleteOpen = () => {
    this.setStateSafe({ deleteDialogOpen: true });
  };

  private _handleDeleteClose = () => {
    this.setStateSafe({ deleteDialogOpen: false });
  };

  private _getNamespace(): string {
    // TODO: We should probably check if all configs have the same namespace.
    return this.props.configs[0]?.namespace || '';
  }

  private _buildUrl(): string {
    const urls = this.props.configs.map((c) => c.url).sort();
    return urls.length === 1 ? urls[0] : urls.map((c, i) => `Series${i + 1}:` + c).join(',');
  }

  private _podTemplateSpec(): any | undefined {
    const podTemplateSpec = this.props.configs[0]?.podTemplateSpec;
    // TODO: how to handle multiple config with different pod template specs?
    return podTemplateSpec || undefined;
  }

  private _image(): string | undefined {
    return this.props.configs[0]?.image || undefined;
  }

  private async _checkTensorboardPodStatus(): Promise<void> {
    // If the proxied TensorBoard is not ready yet, poll the scoped proxy path again.
    if (this.state.proxyPath && !this.state.tensorboardReady) {
      Apis.isTensorboardPodReady(this.state.proxyPath).then((ready) => {
        this.setStateSafe(({ tensorboardReady }) => ({
          tensorboardReady: tensorboardReady || ready,
        }));
      });
    }
  }

  private async _checkTensorboardApp(): Promise<void> {
    this.setStateSafe({ busy: true }, async () => {
      try {
        // TODO: parse tfImage here
        const { proxyPath, image } = await Apis.getTensorboardApp(
          this._buildUrl(),
          this._getNamespace(),
        );
        if (proxyPath) {
          this.setStateSafe({ busy: false, proxyPath, tfImage: image });
        } else {
          // No existing pod
          this.setStateSafe({ busy: false });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        this.setStateSafe({ busy: false, errorMessage });
      }
    });
  }

  private _startTensorboard = async () => {
    this.setStateSafe({ busy: true, errorMessage: undefined }, async () => {
      try {
        const proxyPath = await Apis.startTensorboardApp({
          logdir: this._buildUrl(),
          namespace: this._getNamespace(),
          image: this.state.tfImage,
          podTemplateSpec: this._podTemplateSpec(),
        });
        this.setStateSafe({ busy: false, proxyPath, tensorboardReady: false }, () => {
          if (proxyPath) {
            this._checkTensorboardPodStatus();
          } else {
            this._checkTensorboardApp();
          }
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        this.setStateSafe({ busy: false, errorMessage });
      }
    });
  };

  private _deleteTensorboard = async () => {
    // delete the already opened Tensorboard, clear the proxy path recorded in frontend,
    // and return to the select & start tensorboard page
    this.setStateSafe({ busy: true, errorMessage: undefined }, async () => {
      try {
        await Apis.deleteTensorboardApp(this._buildUrl(), this._getNamespace());
        this.setStateSafe({
          busy: false,
          deleteDialogOpen: false,
          proxyPath: '',
          tensorboardReady: false,
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        this.setStateSafe({ busy: false, errorMessage });
      }
    });
  };

  private setStateSafe(
    newState:
      | Partial<TensorboardViewerState>
      | ((prevState: TensorboardViewerState) => Partial<TensorboardViewerState>),
    cb?: () => void,
  ): void {
    if (this._isMounted) {
      this.setState(newState as any, cb);
    }
  }
}

export default TensorboardViewer;
